import { Grid, Box, Typography } from '@mui/material';
import React from 'react';
import { useSparkSearchContext } from 'hooks/common/useSparkSearchContextV2';
import { useSparkSearch } from 'hooks/spark';
import HistoryCardSkeleton from 'sharedComponents/Skeleton/HistoryCardSkeleton';
import { InfoCard, SparkDataTable } from 'sharedComponents/UI';
import RunSparkModal from './Modals/RunSparkModal';
import SparkdefaultIcon from 'components/SVG/SparkdefaultIcon';
import SparkBadges from 'components/common/SparkBadges';
import { SPARK_BADGE_TYPES } from 'constants/common';
import SparkAvatarBackground from 'sharedComponents/UI/SparkAvatarBackground';
import { Column } from 'sharedComponents/UI/SparkDataTable';
import { formatDateAndTime } from 'utilities/time';
import SparkRowOptions from './SparkNewContent/SparkRowOptions';
import { useDialog } from 'hooks/common';
import { useSparkPrivacyTypes } from 'hooks/EntityTypes/useSparkPrivacyTypes';
import Pagination from 'sharedComponents/Pagination';

type SparkRow = {
  id: string;
  name: string;
  iconUrl: string;
  updatedAt: string;
  privacy: string;
};

function SparksSearchViewContent() {
  const { filter } = useSparkSearchContext();
  const [page, setPage] = React.useState(1);

  const [selectedSparkID, setSelectedSparkID] = React.useState<string>();

  const {
    data: joinedSparks = [],
    isLoading: isSearchingSparks,
    isFetching: isFetchingSparkSearch,
  } = useSparkSearch({
    filters: filter,
    pagination: { pageNumber: page, pageSize: 10 },
  });
  const totalCount = joinedSparks[0]?.totalCount;

  const { idMap, isLoading: isSparkPrivacyTypesLoading } =
    useSparkPrivacyTypes();

  const {
    openDialog: openSparkRunDialog,
    closeDialog: closeSparkRunDialog,
    open: isSparkRunDialogOpen,
  } = useDialog();

  const handleRowClick = (sparkID: string) => {
    setSelectedSparkID(sparkID);
    openSparkRunDialog();
  };

  const columns: Column<SparkRow>[] = [
    {
      field: 'name',
      headerText: 'Name',
      flex: 3,
      sortable: true,
      cellRenderer: (row) => {
        const spark = joinedSparks?.find(
          (joinedSpark) => joinedSpark.id === row.id
        );
        const badge = spark?.meta.badges.filter(
          (badge) => badge.type === SPARK_BADGE_TYPES.Status
        );
        return (
          <Box display='flex' alignItems='center' gap={2}>
            <SparkAvatarBackground
              src={row.iconUrl || ''}
              icon={<SparkdefaultIcon />}
              width='32px'
              height='32px'
            />
            <Typography
              variant='body1'
              sx={{
                color: '#000000',
                textWrap: 'nowrap',
              }}
            >
              {row.name || '- -'}
            </Typography>
            {!!badge && <SparkBadges badges={badge} />}
          </Box>
        );
      },
    },
    {
      field: 'updatedAt',
      headerText: 'Updated',
      sortable: true,
      cellRenderer: (row) => {
        return (
          <Box display='flex' alignItems='center' gap={2}>
            <Typography
              variant='body1'
              sx={{
                color: '#000000',
              }}
            >
              {row.updatedAt || '- -'}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'privacy',
      headerText: 'Privacy',
      sortable: true,
      cellRenderer: (row) => {
        return (
          <Box display='flex' alignItems='center' gap={2}>
            <Typography
              variant='body1'
              sx={{
                color: '#000000',
              }}
            >
              {row.privacy || '- -'}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: '',
      headerText: '',
      cellRenderer: (row) => {
        const joinedSpark = joinedSparks?.find(
          (joinedSpark) => joinedSpark.id === row.id
        );
        return (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              paddingX: 2,
              width: '100%',
            }}
          >
            <span
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              <SparkRowOptions joinedSpark={joinedSpark} />
            </span>
          </Box>
        );
      },
    },
  ];
  const rows: SparkRow[] | undefined = joinedSparks?.map((spark) => {
    return {
      id: spark.id,
      iconUrl: spark?.attributes.iconURL || '',
      name: spark?.attributes.nameforHuman || '',
      updatedAt: formatDateAndTime(spark.attributes.modifiedAt) || '',
      privacy:
        idMap?.[spark?.attributes.sparkPrivacyTypeID || '']?.attributes.name ||
        '',
    };
  });

  const isLoading =
    isSparkPrivacyTypesLoading || isSearchingSparks || isFetchingSparkSearch;

  return (
    <Grid container gap={2}>
      {isLoading ? (
        <HistoryCardSkeleton />
      ) : (
        <Grid container direction='column' gap={2}>
          <Typography
            fontFamily='Mont-Semibold'
            variant='h6'
            marginLeft={0.5}
          >{`${totalCount || 0} ${
            totalCount === 1 ? 'result' : 'results'
          }`}</Typography>
          {!!rows.length ? (
            <Box display='flex' flexDirection='column' gap={2}>
              <SparkDataTable<SparkRow>
                columns={columns}
                rows={rows || []}
                isRowClickable={true}
                pagination={false}
                onRowClick={handleRowClick}
                showLeftChevron={false}
              />
              {totalCount && totalCount > 10 && (
                <Pagination
                  currentPage={page}
                  setCurrentPage={setPage}
                  dataCount={totalCount || 0}
                  itemsPerPage={10}
                />
              )}
            </Box>
          ) : (
            <Box width='100%'>
              <InfoCard info={'No Sparks found'} type='error' />
            </Box>
          )}
        </Grid>
      )}
      {isSparkRunDialogOpen && selectedSparkID && (
        <RunSparkModal
          isSparkRunDialogOpen={isSparkRunDialogOpen}
          closeSparkRunDialog={closeSparkRunDialog}
          sparkID={selectedSparkID}
        />
      )}
    </Grid>
  );
}

export default SparksSearchViewContent;
